import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography } from '@mui/material';
import { TbVideo, TbPlus } from 'react-icons/tb';
import { MdCalendarToday, MdAccessTime } from 'react-icons/md';
import ZoomImage from '../../../media/site_media/platform-icons/zoom.png';
import MicrosoftTeams from '../../../media/site_media/platform-icons/microsoft-teams.png';
import GoogleMeetImage from '../../../media/site_media/platform-icons/google-meet.png';
import { stripHtmlTags } from '../../../utils/stripHtmlTags';
import { normalizeExternalHref } from '../../../utils/normalizeExternalHref';

const EventPost = React.memo(function EventPost({
   content_type,
   link,
   text,
   community_post_title,
   time,
   post_id,
   hideLocation = false,
   imageSrc = null,
   is_example = false,
}) {
   const { t } = useTranslation();
   const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
   const [eventDate, setEventDate] = useState(null);
   const [eventTimeDelta, setEventTimeDelta] = useState('...');
   const [eventDateStr, setEventDateStr] = useState('');
   const [isPast, setIsPast] = useState(false);

   useEffect(() => {
      if (!time) {
         setEventTimeDelta(t('community.event.noTimeSpecified'));
         return;
      }

      const eventDateTime = new Date(time);

      // Validate date
      if (isNaN(eventDateTime.getTime())) {
         setEventTimeDelta(t('community.event.invalidDate'));
         return;
      }
      
      setEventDate(eventDateTime);
      
      // Format date in user's local timezone
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      
      const dayOfWeek = days[eventDateTime.getDay()];
      const dayOfMonth = eventDateTime.getDate();
      const month = months[eventDateTime.getMonth()];
      const year = eventDateTime.getFullYear();
      
      // Format time in 12-hour format (local time)
      const hours = eventDateTime.getHours();
      const minutes = eventDateTime.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      const displayMinutes = minutes.toString().padStart(2, '0');
      const timeStr = `${displayHours}:${displayMinutes} ${ampm}`;
      
      setEventDateStr(`${timeStr}, ${dayOfWeek}, ${month} ${dayOfMonth}, ${year}`);
      
      // Format time delta function
      const formatTimeDelta = (diff) => {
         if (diff <= 0) {
            return t('community.event.started');
         }

         const deltaDays = Math.floor(diff / (1000 * 60 * 60 * 24));
         const deltaHours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
         const deltaMinutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

         if (deltaDays > 0) {
            return deltaDays === 1 ? t('community.event.in1Day') : t('community.event.inDays', { count: deltaDays });
         } else if (deltaHours > 0) {
            return deltaHours === 1 ? t('community.event.in1Hour') : t('community.event.inHours', { count: deltaHours });
         } else if (deltaMinutes > 0) {
            return deltaMinutes === 1 ? t('community.event.in1Minute') : t('community.event.inMinutes', { count: deltaMinutes });
         } else {
            return t('community.event.inLessThanAMinute');
         }
      };
      
      // Calculate countdown
      const updateCountdown = () => {
         const now = new Date();
         const diff = eventDateTime - now;
         
         if (diff <= 0) {
            setIsPast(true);
            setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
            setEventTimeDelta(t('community.event.started'));
            return;
         }
         
         setIsPast(false);
         const countdownDays = Math.floor(diff / (1000 * 60 * 60 * 24));
         const countdownHours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
         const countdownMinutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
         const countdownSeconds = Math.floor((diff % (1000 * 60)) / 1000);
         
         setCountdown({ days: countdownDays, hours: countdownHours, minutes: countdownMinutes, seconds: countdownSeconds });
         setEventTimeDelta(formatTimeDelta(diff));
      };
      
      // Initial update
      updateCountdown();
      const interval = setInterval(updateCountdown, 1000);
      
      return () => clearInterval(interval);
   }, [time]);

   const getPlatformName = () => {
      switch (content_type) {
         case 'mgm': return 'Google Meet';
         case 'mzm': return 'Zoom';
         case 'mmt': return 'Microsoft Teams';
         case 'mot': return 'Meeting';
         default: return 'Meeting';
      }
   };

   const getPlatformImage = () => {
      switch (content_type) {
         case 'mgm': return GoogleMeetImage;
         case 'mzm': return ZoomImage;
         case 'mmt': return MicrosoftTeams;
         default: return null;
      }
   };

   const handleAddToCalendar = () => {
      if (is_example) return;
      if (!eventDate || !link || !community_post_title) return;

      const titlePlain = stripHtmlTags(community_post_title);
      
      // Format dates for ICS (YYYYMMDDTHHmmssZ)
      const formatICSDate = (date) => {
         const year = date.getUTCFullYear();
         const month = String(date.getUTCMonth() + 1).padStart(2, '0');
         const day = String(date.getUTCDate()).padStart(2, '0');
         const hours = String(date.getUTCHours()).padStart(2, '0');
         const minutes = String(date.getUTCMinutes()).padStart(2, '0');
         const seconds = String(date.getUTCSeconds()).padStart(2, '0');
         return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
      };
      
      const startDate = formatICSDate(eventDate);
      const endDate = formatICSDate(new Date(eventDate.getTime() + 60 * 60 * 1000)); // 1 hour later
      
      // Escape special characters for ICS format
      const escapeICS = (text) => {
         return text ? text.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n') : '';
      };
      
      // Generate ICS file
      const icsContent = [
         'BEGIN:VCALENDAR',
         'VERSION:2.0',
         'PRODID:-//WithMe//Event//EN',
         'BEGIN:VEVENT',
         `DTSTART:${startDate}`,
         `DTEND:${endDate}`,
         `SUMMARY:${escapeICS(titlePlain)}`,
         `DESCRIPTION:${escapeICS(text || '')}\\n${escapeICS(link)}`,
         `LOCATION:${escapeICS(link)}`,
         `URL:${link}`,
         'STATUS:CONFIRMED',
         'SEQUENCE:0',
         'BEGIN:VALARM',
         'TRIGGER:-PT15M',
         'ACTION:DISPLAY',
         `DESCRIPTION:Reminder: ${escapeICS(titlePlain)}`,
         'END:VALARM',
         'END:VEVENT',
         'END:VCALENDAR'
      ].join('\r\n');
      
      const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const linkEl = document.createElement('a');
      linkEl.href = url;
      linkEl.download = `${(titlePlain || 'event').replace(/[^a-z0-9]/gi, '_').toLowerCase()}.ics`;
      document.body.appendChild(linkEl);
      linkEl.click();
      document.body.removeChild(linkEl);
      URL.revokeObjectURL(url);
   };

   const platformImage = getPlatformImage();
   const platformName = getPlatformName();
   const eventTitlePlain = stripHtmlTags(community_post_title);
   const eventTitleDisplay = eventTitlePlain || 'Event';

   return (
      <Box
         sx={{
            backgroundColor: 'transparent',
            padding: '0px',
            marginBottom: '16px'
         }}
      >
         {/* Header */}
         <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
            {/* Icon */}
            <div
               style={{
                  width: '40px',
                  height: '40px',
                  // backgroundColor: '#F9FBFF',
                  borderRadius: '12px',
                  // border: '1px solid #E4EBF6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '10px',
                  flexShrink: 0
               }}
            >
              {platformImage ? (
                  <img src={platformImage} alt={platformName} style={{ width: '40px', height: '40px' }} />
               ) : (
                  <TbVideo style={{ fontSize: '40px', color: '#6B7280' }} />
               )}
            </div>
            
            {/* Title and Platform */}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', height: '48px' }}>
               <Typography
                  variant="h6"
                  style={{
                     fontWeight: 'bold',
                     fontSize: '16px',
                     color: '#1D1D1D',
                     lineHeight: '1.3'
                  }}
               >
                  {eventTitleDisplay}
               </Typography>
            </div>
         </div>

         {/* Optional event image */}
         {imageSrc && (
            <div style={{ marginBottom: '18px' }}>
               <img
                  src={imageSrc}
                  alt={eventTitleDisplay}
                  style={{
                     width: '100%',
                     maxHeight: '400px',
                     objectFit: 'contain',
                     borderRadius: '12px',
                     display: 'block',
                  }}
               />
            </div>
         )}

         {/* Description */}
         {text && (
            <Typography
               variant="body2"
               style={{
                  color: '#4B5563',
                  fontSize: '15px',
                  lineHeight: '1.6',
                  marginBottom: '20px',
                  whiteSpace: 'pre-wrap'
               }}
               dangerouslySetInnerHTML={{ __html: text }}
            />
         )}

         {/* Event Details */}
         {time && (
            <>
               <div style={{ marginBottom: '20px' }}>
                  {eventDateStr && (
                     <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <MdCalendarToday style={{ fontSize: '18px', color: '#6C89AF', marginRight: '10px' }} />
                        <Typography variant="body2" style={{ color: '#374151', fontSize: '14px', fontWeight: '500' }}>
                           {eventDateStr}
                        </Typography>
                     </div>
                  )}
                  {/* <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                     <MdAccessTime style={{ fontSize: '18px', color: '#6C89AF', marginRight: '10px' }} />
                     <Typography variant="body2" style={{ color: '#374151', fontSize: '14px', fontWeight: '500' }}>
                        {eventTimeDelta}
                     </Typography>
                  </div> */}
               </div>

               {/* Countdown Timer - Always show when event hasn't passed */}
               {/* {!isPast && ( */}
                  <Box
                     sx={{
                        backgroundColor: '#F9FBFF',
                        border: '1px solid #E4EBF6',
                        borderRadius: '12px',
                        padding: '20px 0px',
                        width: '100%',
                        margin: '0 auto',
                        marginBottom: '20px',
                        textAlign: 'center'
                     }}
                  >
                     <Typography
                        variant="body2"
                        style={{
                           color: '#6B7280',
                           fontSize: '13px',
                           fontWeight: '500',
                           marginBottom: '12px'
                        }}
                     >
                        Event Starting Soon
                     </Typography>
                     <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                           <Typography
                              variant="h4"
                              style={{
                                 fontWeight: 'bold',
                                 fontSize: '32px',
                                 color: '#1D1D1D',
                                 lineHeight: '1'
                              }}
                           >
                              {countdown.days.toString().padStart(2, '0')}
                           </Typography>
                           <Typography variant="caption" style={{ color: '#6B7280', fontSize: '12px', marginTop: '4px' }}>
                              days
                           </Typography>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                           <Typography
                              variant="h4"
                              style={{
                                 fontWeight: 'bold',
                                 fontSize: '32px',
                                 color: '#1D1D1D',
                                 lineHeight: '1'
                              }}
                           >
                              {countdown.hours.toString().padStart(2, '0')}
                           </Typography>
                           <Typography variant="caption" style={{ color: '#6B7280', fontSize: '12px', marginTop: '4px' }}>
                              hours
                           </Typography>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                           <Typography
                              variant="h4"
                              style={{
                                 fontWeight: 'bold',
                                 fontSize: '32px',
                                 color: '#1D1D1D',
                                 lineHeight: '1'
                              }}
                           >
                              {countdown.minutes.toString().padStart(2, '0')}
                           </Typography>
                           <Typography variant="caption" style={{ color: '#6B7280', fontSize: '12px', marginTop: '4px' }}>
                              min
                           </Typography>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                           <Typography
                              variant="h4"
                              style={{
                                 fontWeight: 'bold',
                                 fontSize: '32px',
                                 color: '#1D1D1D',
                                 lineHeight: '1'
                              }}
                           >
                              {countdown.seconds.toString().padStart(2, '0')}
                           </Typography>
                           <Typography variant="caption" style={{ color: '#6B7280', fontSize: '12px', marginTop: '4px' }}>
                              sec
                           </Typography>
                        </div>
                     </div>
                  </Box>
               {/* )} */}
            </>
         )}

         {/* Action Buttons */}
         <div style={{ display: 'flex', gap: '12px' }}>
            <Button
               variant="contained"
               fullWidth
               // Joinable at any time. This used to be gated on a join window that
               // opened only in the hour BEFORE the start and closed the moment the
               // event began — so a live call could not be joined. The countdown
               // above still shows when it starts.
               disabled={is_example}
               onClick={(e) => {
                  e.stopPropagation();
                  if (is_example) return;
                  window.open(normalizeExternalHref(link), '_blank', 'noopener,noreferrer');
               }}
               sx={{
                  backgroundColor: 'var(--brand-color)',
                  color: '#fff',
                  textTransform: 'none',
                  fontWeight: '600',
                  fontSize: '15px',
                  padding: '12px 24px',
                  borderRadius: '10px',
                  boxShadow: 'none',
                  '&:hover': {
                     backgroundColor: 'var(--brand-color-hover)',
                     boxShadow: 'none'
                  },
                  '&:disabled': {
                     backgroundColor: '#E5E7EB',
                     color: '#9CA3AF'
                  }
               }}
            >
               {t('community.event.joinEvent')}
            </Button>
            <Button
               variant="outlined"
               fullWidth
               onClick={(e) => {
                  e.stopPropagation();
                  handleAddToCalendar();
               }}
               startIcon={<TbPlus style={{ fontSize: '18px' }} />}
               sx={{
                  borderColor: '#E4EBF6',
                  color: '#1D1D1D',
                  textTransform: 'none',
                  fontWeight: '500',
                  fontSize: '15px',
                  padding: '12px 24px',
                  borderRadius: '10px',
                  '&:hover': {
                     borderColor: '#C7D1DE',
                     backgroundColor: '#F9FAFB'
                  }
               }}
            >
               Add to Calendar
            </Button>
         </div>
      </Box>
   );
})

export default EventPost;

